
import { useState, useEffect, useCallback } from "react"; // Added useCallback
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { addMonths } from "date-fns";

export const useCreditsReset = () => {
  const { user } = useAuth();
  const [resetDate, setResetDate] = useState<string | null>(null);
  const [nextResetDate, setNextResetDate] = useState<string | null>(null);
  const [resetAmount] = useState<number>(25);
  const [loading, setLoading] = useState<boolean>(true);

  const calculateNextResetDate = (currentResetDate: string) => {
    const now = new Date();
    const resetDateObj = new Date(currentResetDate);
    
    if (resetDateObj < now) {
      return addMonths(resetDateObj, 1).toISOString();
    }
    return currentResetDate;
  };

  const fetchResetDate = useCallback(async () => { // Wrapped in useCallback
    if (!user?.id) {
      setLoading(false); // Ensure loading is set to false if no user
      return;
    }

    setLoading(true); // Set loading to true at the start of fetch
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("credit_reset_date")
        .eq("id", user.id)
        .single();

      if (error) {
        // Error handling without console.log
        setResetDate(null); // Clear dates on error
        setNextResetDate(null);
        return;
      }

      if (data?.credit_reset_date) {
        setResetDate(data.credit_reset_date);
        const nextDate = calculateNextResetDate(data.credit_reset_date);
        setNextResetDate(nextDate);
      } else {
        setResetDate(null); // Clear dates if not found
        setNextResetDate(null);
      }
    } catch (err) {
      // Error handling without console.log
      setResetDate(null);
      setNextResetDate(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // Added user?.id as dependency

  useEffect(() => {
    fetchResetDate();

    const interval = setInterval(() => {
      fetchResetDate();
    }, 24 * 60 * 60 * 1000); // once daily

    return () => clearInterval(interval);
  }, [fetchResetDate]); // Changed dependency to fetchResetDate

  // Export the refetch function
  return { resetDate, nextResetDate, resetAmount, loading, refetchResetDate: fetchResetDate };
};

